import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Copy, Check, ExternalLink } from 'lucide-react';

export default function ZohoFormCreateModal({ open, onOpenChange, categories, onFormCreated }) {
  const [formName, setFormName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('none');
  const [saving, setSaving] = useState(false);
  const [createdForm, setCreatedForm] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setFormName('');
    setDescription('');
    setCategoryId('none');
    setCreatedForm(null);
    setCopied(false);
  };

  const handleClose = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const handleCreate = async () => {
    if (!formName.trim()) {
      toast.error('Form name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await zohoAPI.createForm({
        form_name: formName.trim(),
        description: description.trim() || null,
        category_id: categoryId === 'none' ? null : categoryId,
      });
      setCreatedForm(res.data);
      toast.success('Form created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create form');
    }
    setSaving(false);
  };

  const handleDone = () => {
    if (createdForm) onFormCreated(createdForm);
    reset();
    onOpenChange(false);
  };

  const webhookUrl = createdForm ? zohoAPI.getWebhookUrl(createdForm.slug) : '';

  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success('Webhook URL copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="create-form-modal">
        <DialogHeader>
          <DialogTitle>{createdForm ? 'Form Created' : 'Create New Form'}</DialogTitle>
          <DialogDescription>
            {createdForm
              ? 'Your form has been created. Copy the webhook URL below and paste it into your Zoho Form settings.'
              : 'Create a form configuration to receive webhook submissions from Zoho.'}
          </DialogDescription>
        </DialogHeader>

        {!createdForm ? (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="form-name">Form Name *</Label>
              <Input
                id="form-name"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="e.g., Employee Onboarding Form"
                data-testid="form-name-input"
              />
            </div>
            <div>
              <Label htmlFor="form-desc">Description</Label>
              <Input
                id="form-desc"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional description"
                data-testid="form-desc-input"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="form-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-muted-foreground text-xs">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-3 bg-muted rounded-md text-sm break-all font-mono border" data-testid="created-webhook-url">
                  {webhookUrl}
                </code>
                <Button variant="outline" size="icon" onClick={copyUrl} data-testid="copy-created-url">
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                Paste this URL in your Zoho Form's webhook/notification settings
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!createdForm ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving || !formName.trim()} data-testid="create-form-submit">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Form
              </Button>
            </>
          ) : (
            <Button onClick={handleDone} data-testid="form-created-done">
              Done — Go to Form
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
